import React, { useState, useEffect, useRef } from "react";
import {
  Sparkles,
  FlaskConical, // Chemistry
  Atom, // Physics
  Dna, // Biology
  Zap, // Electricity/Energy
  Leaf, // Plants/Ecology
  TestTube, // Experiments
  BookOpen,
  Mic,
  Volume2,
  Pause,
  Play,
  Square,
} from "lucide-react";


// NOTE: These local imports will only work in your environment, 
// so the image src props in the data mocks will refer to these
import energy from "../../Photos/Science/energy.png"
import machines from "../../Photos/Science/machines.png"
import gravity from "../../Photos/Science/gravity.png"

import cell from "../../Photos/Science/cells.png"
import plant from "../../Photos/Science/plant.png"
import foodchain from "../../Photos/Science/foodchain.png"

import matter from "../../Photos/Science/matter.png"
import atoms from "../../Photos/Science/atoms.png"
import mixture from "../../Photos/Science/mixture.png"



// ======================= SPEECH UTILITY FUNCTION (Reused) =======================
const speakText = (text, selectedVoice) => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    // Slightly lower rate/pitch for a friendly, lesson-like tone
    utterance.rate = 0.8; 
    utterance.pitch = 1.2;

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    window.speechSynthesis.speak(utterance);
  } else {
    console.warn("Speech Synthesis not supported in this browser.");
  }
};

// ======================= DATA MOCKS (Reused & Colors Adjusted for New Theme) =======================
const SCIENCE_TOPICS = {
  Physics: [
    {
      key: "energy",
      title: "Forms of Energy",
      icon: <Zap className="w-5 h-5" />,
      color: "text-sky-600",
      imageSrc: energy,
      content:
        "Energy is the ability to do work. It exists in many forms like kinetic (motion), potential (stored), thermal (heat), and light.",
    },
    {
      key: "gravity",
      title: "Gravity & Weight",
      icon: <Atom className="w-5 h-5" />,
      color: "text-sky-600",
      imageSrc: gravity,
      content:
        "Gravity is the force that pulls objects toward each other. On Earth, it gives objects weight. It's what keeps your feet on the ground!",
    },
    {
      key: "simple_machines",
      title: "Simple Machines",
      icon: <TestTube className="w-5 h-5" />,
      color: "text-sky-600",
      imageSrc: machines,
      content:
        "Simple machines (like levers, wheels, and pulleys) change the direction or magnitude of a force to make work easier.",
    },
  ],

  Biology: [
    {
      key: "cells",
      title: "What are Cells?",
      icon: <Dna className="w-5 h-5" />,
      color: "text-green-600",
      imageSrc: cell,
      content:
        "Cells are the basic building blocks of all living things. Some organisms, like bacteria, have one cell, while humans have trillions!",
    },
    {
      key: "plants",
      title: "Plant Life Cycle",
      icon: <Leaf className="w-5 h-5" />,
      color: "text-green-600",
      imageSrc: plant,
      content:
        "Plants grow from seeds, develop into seedlings, mature, reproduce flowers/seeds, and the cycle begins again. This is called the life cycle.",
    },
    {
      key: "food_chain",
      title: "The Food Chain",
      icon: <BookOpen className="w-5 h-5" />,
      color: "text-green-600",
      imageSrc: foodchain,
      content:
        "A food chain shows how energy moves from one living thing to another. It starts with producers (plants) and moves to consumers (animals).",
    },
  ],

  Chemistry: [
    {
      key: "matter",
      title: "States of Matter",
      icon: <FlaskConical className="w-5 h-5" />,
      color: "text-yellow-600",
      imageSrc: matter,
      content:
        "Matter can be a solid (fixed shape, like ice), liquid (takes container shape, like water), or gas (fills all space, like steam).",
    },
    {
      key: "elements",
      title: "Elements & Atoms",
      icon: <Atom className="w-5 h-5" />,
      color: "text-yellow-600",
      imageSrc: atoms,
      content:
        "An atom is the smallest unit of an element. Elements (like Oxygen and Gold) cannot be broken down into simpler substances.",
    },
    {
      key: "mixtures",
      title: "Mixtures vs. Compounds",
      icon: <TestTube className="w-5 h-5" />,
      color: "text-yellow-600",
      imageSrc: mixture,
      content:
        "A mixture can be separated (like sand and water), but a compound is chemically bonded (like H2O, which is water).",
    },
  ],
};



// ======================= ACTIVITY DISPLAY COMPONENT =======================
const ScienceActivityDisplay = ({ topic, selectedVoice, selectedDomain }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const utteranceRef = useRef(null);
  
  // Clean up any speaking when component is unmounted or topic changes
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, [topic]);

  // --- Function to START or RESUME playback ---
  const handlePlay = () => {
    if (!topic || !topic.content) return;

    // 1. If currently paused -> resume
    if (speechSynthesis.paused) {
      speechSynthesis.resume();
      setIsPlaying(true);
      return;
    }

    // 2. If already speaking, stop and restart with the new topic's content
    speechSynthesis.cancel();
    
    // 3. Start new utterance
    const utterance = new SpeechSynthesisUtterance(topic.content);
    utterance.rate = 0.9;
    utterance.pitch = 1.2;
    if (selectedVoice) utterance.voice = selectedVoice;

    utterance.onend = () => setIsPlaying(false);
    utteranceRef.current = utterance;

    speechSynthesis.speak(utterance);
    setIsPlaying(true);
  };


  // --- Function to PAUSE playback ---
  const handlePause = () => {
    if (speechSynthesis.speaking && !speechSynthesis.paused) {
      speechSynthesis.pause();
      setIsPlaying(false);
    }
  };

  // --- Function to STOP/CANCEL playback and reset state ---
  const handleStop = () => {
    // This completely stops and clears the queue
    speechSynthesis.cancel();
    setIsPlaying(false);
  };
  
  if (!topic) {
    return (
      <div className="text-center p-12 md:p-20 bg-gray-50 rounded-xl shadow-inner text-gray-700 border-2 border-dashed border-gray-200 h-full flex flex-col justify-center items-center">
        <FlaskConical className="w-12 h-12 text-sky-500 mx-auto mb-4 animate-pulse" />
        <h2 className="text-2xl md:text-3xl font-extrabold mb-2">Select a Topic</h2>
        <p className="text-base md:text-lg">
          Choose any topic from the list to view its explanation and concept visualizer here.
        </p>
      </div>
    );
  }

  // Determine the domain icon based on the selected domain
  const domainIcon = selectedDomain === "Physics" ? <Atom /> : selectedDomain === "Biology" ? <Dna /> : <FlaskConical />;

  // Extract base color (e.g., 'sky') and shade (e.g., '600') from the topic's color string
  const colorParts = topic.color.split('-');
  const baseColor = colorParts[1]; 
  const colorShade = colorParts[2]; 
  const primaryClass = `text-${baseColor}-${colorShade}`;
  const lightBgClass = `bg-${baseColor}-50`;
  const borderClass = `border-${baseColor}-400`;


  return (
    <div className="bg-white p-6 md:p-8 rounded-xl shadow-2xl border-t-8 border-sky-500 min-h-[70vh]">
      {/* Topic Header and Read Aloud */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b pb-4 mb-6">
        <div className="flex items-center space-x-3 mb-4 sm:mb-0">
          {React.cloneElement(topic.icon, { className: `w-6 h-6 md:w-7 md:h-7 ${primaryClass}` })}
          <h3 className={`text-2xl md:text-3xl font-extrabold text-gray-800`}>
            {topic.title}
          </h3>
          <span className={`text-xs md:text-sm font-semibold px-3 py-1 rounded-full ${lightBgClass} ${primaryClass} border ${borderClass} hidden sm:inline-block`}>
            {selectedDomain}
          </span>
        </div>
        
        {/* Read Aloud Controls */}
        <div className="flex items-center gap-2">
          {/* 🎧 PLAY BUTTON (Starts or Resumes) */}
          <button
            onClick={handlePlay}
            disabled={isPlaying || !topic}
            className={`p-2 md:p-3 rounded-full shadow-md text-white 
      ${isPlaying ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"} 
      transition`}
            title="Play / Resume"
          >
            <Play className="w-4 h-4 md:w-5 md:h-5" />
          </button>

          {/* ⏸️ PAUSE BUTTON */}
          <button
            onClick={handlePause}
            disabled={!isPlaying}
            className={`p-2 md:p-3 rounded-full shadow-md text-white 
      ${!isPlaying ? "bg-gray-400 cursor-not-allowed" : "bg-yellow-500 hover:bg-yellow-600"} 
      transition`}
            title="Pause"
          >
            <Pause className="w-4 h-4 md:w-5 md:h-5" />
          </button>

          {/* ⏹️ STOP/RESET BUTTON */}
          <button
            onClick={handleStop}
            disabled={!speechSynthesis.speaking && !speechSynthesis.paused}
            className={`p-2 md:p-3 rounded-full shadow-md text-white 
      ${(!speechSynthesis.speaking && !speechSynthesis.paused) ? "bg-gray-400 cursor-not-allowed" : "bg-red-500 hover:bg-red-600"} 
      transition`}
            title="Stop and Reset"
          >
            <Square className="w-4 h-4 md:w-5 md:h-5" /> 
          </button>

          <Volume2 className="w-5 h-5 text-gray-800 ml-1 hidden sm:block" />
        </div>
      </div>

      <div className="space-y-6">
        {/* Lesson Content Area */}
        <div className={`text-base md:text-lg text-gray-700 leading-relaxed ${lightBgClass} p-4 md:p-5 rounded-lg border-l-4 ${borderClass}`}>
          <p className="font-medium mb-2">Key Concept:</p>
          <p>{topic.content}</p>
        </div>

        {/* Interactive Element / Diagram Placeholder (Modified for structure and image handling) */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <h4 className={`text-xl font-bold ${primaryClass} mb-3 flex items-center`}>
            {React.cloneElement(domainIcon, { className: `w-5 h-5 mr-2 ${primaryClass}` })}
            Visualizer:
          </h4>
          
          {/* Visualizer Container - Enforces a 16:9 aspect ratio */}
          <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center p-3 sm:p-6 border-2 border-dashed border-gray-300 overflow-hidden">
            {/* Conditional logic for image based on local or placeholder src */}
            {topic.imageSrc ? (
  <img
  src={topic.imageSrc}
  alt={`${topic.title} Diagram`}
  loading="lazy"
  decoding="async"
  className="w-full h-full object-contain"
/>

            ) : (
              <p className="text-gray-600 italic text-sm sm:text-base">
                [Diagram/Simulation Placeholder for: {topic.title}]
              </p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};


// ======================= MAIN PAGE COMPONENT =======================
const ScienceBasicsPage = () => {
  const [selectedDomain, setSelectedDomain] = useState("Physics");
  const [selectedTopic, setSelectedTopic] = useState(SCIENCE_TOPICS.Physics[0]);

  const [voices, setVoices] = useState([]);
  const [preferredVoice, setPreferredVoice] = useState(null);
  const voiceListRef = useRef([]);

  // ASYNCHRONOUS VOICE LOADING
  useEffect(() => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const currentVoices = window.speechSynthesis.getVoices();
        if (currentVoices.length > 0 && voiceListRef.current.length === 0) {
          voiceListRef.current = currentVoices;
          setVoices(currentVoices);

          // Attempt to find a preferred voice (kid-friendly or standard English)
          const preferred = currentVoices.find(v =>
            v.lang.startsWith('en-') &&
            (v.name.includes('Kid') || v.name.includes('Zarvox') || v.name.includes('US English'))
          ) || currentVoices.find(v => v.lang.startsWith('en-'));

          setPreferredVoice(preferred);
        }
      };

      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = loadVoices;
      }
      loadVoices();
    }
  }, []);

  const handleDomainSelect = (domain) => {
    setSelectedDomain(domain);
    // Select the first topic in the new domain
    setSelectedTopic(SCIENCE_TOPICS[domain][0]);
  };

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    // Speak the title when a new topic is selected
    speakText(topic.title, preferredVoice);
  };

  const handleVoiceChange = (event) => {
    const voiceName = event.target.value;
    const newVoice = voices.find(v => v.name === voiceName);
    setPreferredVoice(newVoice);
    // Test the new voice setting
    speakText("Testing voice settings.", newVoice);
  };

  // Custom Tailwind colors/classes for the new soft theme (For reference, but Tailwind handles classes)
  // These are not needed if Tailwind is configured, but kept as a reminder of the theme colors.
  // const customStyles = `
  //   .text-sky-600 { color: #0284c7; }
  //   .bg-sky-500 { background-color: #0ea5e9; }
  //   ...
  // `;

  return (
    <div className="min-h-screen bg-[#F3F6FA] flex flex-col">
      {/* Inject custom CSS for colors if needed outside of a proper Tailwind setup, 
          though it's usually better handled by Tailwind config or utility classes. 
          <style>{customStyles}</style>
      */}

      {/* ======= HEADER ======= */}
      <header className="w-full bg-white shadow-md border-b border-gray-200 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2 md:gap-3">
          <FlaskConical className="w-7 h-7 md:w-9 md:h-9 text-sky-500" />
          <h1 className="text-xl md:text-3xl font-extrabold text-gray-800 tracking-tight">
            Science Explorer
          </h1>
        </div>

        {/* Voice Selector */}
        {"speechSynthesis" in window && (
          <div className="flex items-center gap-2 bg-gray-100 p-1 md:p-2 rounded-lg shadow-sm">
            <Mic className="w-4 h-4 md:w-5 md:h-5 text-sky-600" />
            <select
              id="voice-select"
              onChange={handleVoiceChange}
              value={preferredVoice ? preferredVoice.name : ""}
              className="p-1 md:p-2 rounded bg-white border border-gray-300 text-xs md:text-sm text-gray-800 min-w-28 md:min-w-32 focus:ring-sky-500 focus:border-sky-500"
            >
              {voices.length === 0 ? (
                <option>Loading voices...</option>
              ) : (
                voices.map((voice) => (
                  <option key={voice.name} value={voice.name}>
                    {voice.name}
                  </option>
                ))
              )}
            </select>
          </div>
        )}
      </header>

      {/* ======= DOMAIN TABS (Scrollable on small screens) ======= */}
      <div className="bg-white w-full px-4 md:px-6 py-2 border-b border-gray-200 flex gap-2 md:gap-4 overflow-x-auto whitespace-nowrap sticky top-[57px] md:top-[69px] z-30">
        {Object.keys(SCIENCE_TOPICS).map((domain) => {
          let icon =
            domain === "Physics" ? (
              <Atom />
            ) : domain === "Biology" ? (
              <Dna />
            ) : (
              <FlaskConical />
            );
          let baseColor =
            domain === "Physics"
              ? "sky"
              : domain === "Biology"
                ? "green"
                : "yellow";

          let active = selectedDomain === domain;

          return (
            <button
              key={domain}
              onClick={() => handleDomainSelect(domain)}
              className={`flex items-center px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold text-sm transition flex-shrink-0
              ${active
                  ? `bg-${baseColor}-50 text-${baseColor}-700 border-b-4 border-${baseColor}-600 shadow-sm`
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }
            `}
            >
              {React.cloneElement(icon, { className: "w-4 h-4 md:w-5 md:h-5 mr-2" })}
              {domain}
            </button>
          );
        })}
      </div>

      {/* ======= MAIN CONTENT ======= */}
      <main className="flex flex-col lg:flex-row w-full gap-6 p-4 md:px-6 md:py-6 flex-1 overflow-y-auto">

        {/* LEFT SIDEBAR — Topic List */}
        {/* On mobile, use max-h-48 and overflow-y-auto for a contained list */}
        <aside className="w-full lg:w-1/3 xl:w-1/4 space-y-4 lg:overflow-y-auto lg:h-auto max-h-96 md:max-h-full">
          <h2 className="text-lg md:text-xl font-bold text-gray-700 border-l-4 border-sky-500 pl-3">
            Topics in <span className="text-sky-600">{selectedDomain}</span>
          </h2>

          <div className="bg-white p-4 md:p-5 rounded-2xl shadow-lg border border-gray-100 space-y-2 overflow-y-auto max-h-96">
            {SCIENCE_TOPICS[selectedDomain].map((topic) => {
              let active = selectedTopic?.key === topic.key;

              const [_, baseColor] = topic.color.split("-");

              return (
                <div
                  key={topic.key}
                  onClick={() => handleTopicSelect(topic)}
                  className={`flex items-center p-3 rounded-xl border-l-4 cursor-pointer transition 
                    ${active
                      ? `bg-${baseColor}-50 border-${baseColor}-500 text-${baseColor}-600 shadow-sm`
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                    }
                `}
                >
                  {React.cloneElement(topic.icon, {
                    className: `w-5 h-5 mr-3 ${active ? `text-${baseColor}-600` : "text-gray-400"
                      }`,
                  })}
                  <span className="text-sm font-medium">{topic.title}</span>
                </div>
              );
            })}
          </div>
        </aside>

        {/* RIGHT — Topic Display */}
        <section className="w-full lg:flex-1 lg:overflow-y-auto">
          <ScienceActivityDisplay
            topic={selectedTopic}
            selectedVoice={preferredVoice}
            selectedDomain={selectedDomain}
          />
        </section>
      </main>
    </div>
  );
};

export default ScienceBasicsPage;